import { useCallback } from "react";

import {
  Box,
  Paper,
  Toolbar,
  ToolbarProps,
  Typography,
  styled,
  useTheme,
} from "@mui/material";

import { SeriesType } from "../hooks/useVisualisationData";
import { HorizontalStack } from "./primitives/Stacks";
import { useAppContext } from "../context/AppContext";

const LegendSymbol = styled(Box)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 4,
  height: theme.spacing(1.25),
  width: theme.spacing(1.25),
}));

const LegendItem = styled(HorizontalStack)(({ theme }) => ({
  cursor: "pointer",
  float: "left",
  padding: theme.spacing(0.25, 0.75),
  transform: theme.transitions.create("opacity"),
}));

export const DatasetLegend: React.FC<
  ToolbarProps & {
    series?: SeriesType[];
    isExtrasVisible: boolean;
  }
> = ({ series, isExtrasVisible, ...rootProps }) => {
  const { legend, setLegend } = useAppContext();

  const theme = useTheme();

  const onToggleSeries = useCallback(
    (seriesId: string) => () => {
      if (legend.hidden?.includes(seriesId)) {
        setLegend((s) => ({
          ...s,
          hidden: (s.hidden || []).filter((id) => id !== seriesId),
        }));
      } else {
        setLegend((s) => ({
          ...s,
          focused: null,
          hidden: [...(s.hidden || []), seriesId],
        }));
      }
    },
    [setLegend, legend.hidden]
  );

  const extrasSerie = {
    id: "extras",
    color: theme.palette.grey[500],
    name: "Positive Experiences / Activities",
  };

  return (
    <Toolbar disableGutters {...rootProps}>
      {series && series?.length > 0 && (
        <Paper variant="outlined" sx={{ flexGrow: 1, py: 0.5, px: 1 }}>
          {[...series, ...(isExtrasVisible ? [extrasSerie] : [])].map(
            (item) => {
              const isHidden = legend?.hidden?.includes(item.id);
              return (
                <LegendItem
                  alignItems="center"
                  key={item.id}
                  onMouseEnter={() => {
                    if (isHidden) return;
                    setLegend((s) => ({ ...s, focused: item.id }));
                  }}
                  onMouseLeave={() =>
                    setLegend((s) => ({ ...s, focused: null }))
                  }
                  spacing={1}
                  onClick={onToggleSeries(item.id)}
                  style={{ marginLeft: 0 }}
                >
                  <LegendSymbol
                    sx={(theme) => ({
                      bgcolor: isHidden
                        ? "divider"
                        : legend.focused === item.id || !legend.focused
                          ? item.color
                          : "rgba(0,0,0,0.1)",
                      transition: theme.transitions.create("background"),
                    })}
                  />
                  <Typography
                    color={isHidden ? "text.disabled" : "text.secondary"}
                    style={{
                      textDecoration: isHidden ? "line-through" : "none",
                    }}
                    variant="body2"
                  >
                    {item.name}
                  </Typography>
                </LegendItem>
              );
            }
          )}
        </Paper>
      )}
    </Toolbar>
  );
};

export default DatasetLegend;
